import { useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { Button, Input } from 'components';
import { firebase, storage } from 'core';
import { Api, checkSpecialChars, theme, validateEmail } from 'lib';
import { AccountLocked, AppleLogin, GgLogin, LoginAttempts } from 'screens/Login/components';
import RateItRatingBar from './RateItRatingBar';
import {
  RATE_IT_AUTH_COLORS,
  RATE_IT_DEFAULT_MILESTONE_COUNT,
  RATE_IT_MILESTONE_COLORS
} from '../constants';

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 45;
  display: flex;
  justify-content: center;
  overflow-y: auto;
  background: ${RATE_IT_AUTH_COLORS.GROUND};
  box-sizing: border-box;
`;

/**
 * The auth screen's column, capped at the stage's own width so it reads as one surface with the
 * feed behind it rather than a form stretched across a desktop viewport.
 */
const Sheet = styled.div`
  position: relative;
  width: 100%;
  max-width: 420px;
  display: flex;
  flex-direction: column;
  min-height: 100%;
`;

/** The referral offer, carried up here as the first thing read. Same copy as the feed banner. */
const OfferBanner = styled.div`
  padding: 12px 20px;
  text-align: center;
  background: ${RATE_IT_MILESTONE_COLORS.CTA};
  font-family: CeraPro, sans-serif;
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  color: #fff;
`;

/**
 * The shared moment, shown PLAIN.
 *
 * It used to be a full-bleed blurred backdrop behind the form — the brief's "keep the shared
 * moment visible behind it (blurred)". The design supersedes that: the moment is a preview strip
 * at the top, unblurred, with the form below it. The intent is better served this way round. A
 * blurred wash proves a moment exists; a preview shows WHICH one, which is the thing that makes
 * signing up worth it to someone who followed a friend's link to see it.
 *
 * Still a STILL, never a playing video: this screen is asking the visitor to fill in a form, and
 * motion above it competes for exactly the attention the form needs — at the cost of a second
 * decoded stream.
 */
const Preview = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 4 / 3;
  max-height: 34vh;
  flex: none;
  background: ${(props) =>
      props.$src ? `url(${props.$src}) center / cover no-repeat` : 'transparent'}
    ${RATE_IT_MILESTONE_COLORS.BG};
`;

/**
 * The rating bar as a PICTURE of itself: filled, and inert.
 *
 * Compact rather than the feed's full-height bar — it is showing the mechanic, not offering it,
 * and at full height it would read as a live control the visitor is being ignored by. The tile
 * they pressed to get here stays lit, so the screen answers "what happened to my tap?" without
 * a line of copy.
 */
const BarPreview = styled.div`
  width: 100%;
  flex: none;
  background: ${RATE_IT_AUTH_COLORS.GROUND};
`;

const FormCard = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 28px 20px calc(28px + env(safe-area-inset-bottom, 0px));
  background: ${RATE_IT_AUTH_COLORS.CARD};
  border-radius: 20px 20px 0 0;
`;

const Title = styled.h2`
  margin: 0 0 8px;
  text-align: center;
  font-family: CeraPro, sans-serif;
  font-size: 24px;
  font-weight: 700;
  color: #fff;
`;

const Subtitle = styled.p`
  margin: 0 0 24px;
  text-align: center;
  font-family: CeraPro, sans-serif;
  font-size: 14px;
  line-height: 20px;
  color: ${RATE_IT_MILESTONE_COLORS.MUTED};
`;

/**
 * Dark field styling, applied from OUTSIDE the shared `Input`.
 *
 * `Input` takes a `bg` but fixes its own text and placeholder colours for a light ground, so
 * `bg` alone would put near-black type on a near-black field. Overriding all three here keeps
 * the change inside this screen — the alternative, teaching the shared input a dark variant,
 * edits a component used on dozens of forms for one caller. Same technique this file already
 * uses on the social buttons.
 */
const Fields = styled.div`
  width: 100%;

  /*
   * The <input> itself must stay TRANSPARENT.
   *
   * The shared Input paints its rounded pill on a container and puts the input element inside
   * it, so a background on the input draws a second, rectangular block within the pill — visible
   * as a grey slab with square corners inset from the border. The pill's own colour comes from
   * the bg prop each field below passes, which is the supported way in.
   * (No backticks in here: this is a styled-components template literal.)
   */
  input {
    background-color: transparent;
    color: #fff;
  }

  input::placeholder {
    color: ${RATE_IT_AUTH_COLORS.PLACEHOLDER};
  }

  /*
   * The focus ring, which the shared input draws in BLACK — invisible on a dark card. Left as a
   * plain rule rather than something cleverer: it also wins over the red error ring while a
   * field with an error is focused, and that is the right trade, since the error message renders
   * in red directly beneath the field either way.
   *
   * :first-child picks the pill and not the error row, which is its sibling.
   */
  > div > div:first-child:focus-within {
    border-color: rgba(255, 255, 255, 0.35);
  }
`;

const SocialButtonWrap = styled.div`
  position: relative;
  width: 100%;
  margin: ${({ marg }) => marg || 0};

  button {
    background-color: #fff;
    border: none;
  }
`;

/**
 * The sponsor opt-in, on SIGN-UP ONLY.
 *
 * Same question `screens/SignUp/components/Auth.js` asks and the same `auth.sponsorConsent` copy,
 * so a fan who arrives through a referral link is asked exactly what a fan who arrives through
 * the front door is asked, and the admin panel's read-only `SponsorConsent` mirror keeps working
 * without a second key to translate.
 *
 * A native checkbox rather than the shared `Checkbox`: that one is built for the marketing pages
 * — a 30px tick absolutely positioned over a 20px circle — and paints in `theme.SECONDARY` on a
 * light ground, both wrong on this dark card. The main sign-up form made the same call.
 */
const ConsentRow = styled.div`
  width: 100%;
  display: flex;
  align-items: flex-start;
  margin: 20px 0 0;
`;

const ConsentBox = styled.input`
  width: 16px;
  height: 16px;
  margin: 1px 0 0;
  flex: none;
  accent-color: ${RATE_IT_MILESTONE_COLORS.CTA};
  cursor: pointer;
`;

/* The label is what most people press, so it carries the cursor and the `for` binding. */
const ConsentLabel = styled.label`
  margin-left: 10px;
  font-family: CeraPro, sans-serif;
  font-size: 13px;
  line-height: 18px;
  color: ${RATE_IT_MILESTONE_COLORS.MUTED};
  cursor: pointer;
`;

const TextDivider = styled.p`
  position: relative;
  margin: 20px 0;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${RATE_IT_MILESTONE_COLORS.MUTED};
  font-family: CeraPro, sans-serif;
  font-size: 12px;
  letter-spacing: 1px;
  text-transform: uppercase;

  &::before,
  &::after {
    position: absolute;
    content: '';
    top: 50%;
    background-color: rgba(255, 255, 255, 0.16);
    width: 42%;
    height: 1px;
  }
  &::before {
    left: 0;
  }
  &::after {
    right: 0;
  }
`;

const SwitchMode = styled.p`
  margin: 20px 0 0;
  text-align: center;
  font-family: CeraPro, sans-serif;
  font-size: 13px;
  color: ${RATE_IT_MILESTONE_COLORS.MUTED};

  button {
    padding: 0 0 0 4px;
    border: none;
    background: transparent;
    color: #fff;
    font-family: inherit;
    font-size: inherit;
    font-weight: 700;
    cursor: pointer;
    text-decoration: underline;
  }
`;

/**
 * The brief's §11 sign-up screen: the referral offer, a preview of the shared moment and its
 * rating bar, then name / email / password.
 *
 * A THIN FORK of `screens/CTAScreenV2/components/CtaAuth.js` rather than a copy of it — the same
 * call sequence (`/users/login`, `/users/signUp`, then `/users/updateUsername` so the generic
 * Onboarding screen never appears), the same inline field-error handling, the same
 * `LoginAttempts` / `AccountLocked` treatment. What differs is only the identity: this one sits
 * over a moment instead of a gradient, and returns the fan to that moment instead of to a
 * shoutout composer.
 *
 * The moment is a PREVIEW at the top rather than a blurred backdrop behind the form, which is
 * what the brief's words ("keep the shared moment visible behind it (blurred)") describe and
 * what this screen shipped as first. The design supersedes it and is right to: a blurred wash
 * proves a moment exists, while a preview shows WHICH one — the thing that makes signing up
 * worth it to someone who followed a friend's link to see it.
 *
 * Not shared with `CtaAuth` as one component: that file is shaped around `otherUser` and a
 * hard-coded shoutout redirect, and generalising it would mean touching a live flow to add a
 * second caller's conditionals. Two focused forks read better here than one branching component.
 *
 * `{ silent: true }` throughout is load-bearing: backend field errors (wrong password, taken
 * username) must render under their own input rather than as the generic global toast, and the
 * wrong-password message lives in `pwErr`, which the global handler cannot surface at all.
 */
const RateItAuth = ({
  item,
  sender,
  points,
  isLogin,
  returnPath,
  /** The tile the visitor pressed to get here, kept lit on the preview bar. */
  pendingRating,
  onAuthComplete,
  onSwitchMode
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { language } = useSelector((state) => state.appState);

  const emailInputRef = useRef(null);
  const passwordInputRef = useRef(null);

  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emailErr, setEmailErr] = useState(null);
  const [usernameErr, setUsernameErr] = useState(null);
  const [passErr, setPassErr] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const [accountLockedVisible, setAccountLockedVisible] = useState(false);
  // Optional, and deliberately absent from `disabled` below — an unticked box never blocks
  // registration, it is recorded as an explicit "no".
  const [sponsorConsent, setSponsorConsent] = useState(false);
  // Set once /users/signUp succeeds, so a retry (e.g. username taken) skips re-signup.
  const [createdUser, setCreatedUser] = useState(null);

  const usernameOnly = !!createdUser;
  const credentialsMissing = !validateEmail(email) || password.length < 8;
  const disabled = isLogin
    ? credentialsMissing
    : !username.length || (!usernameOnly && credentialsMissing);

  /**
   * `checkSpecialChars` ANSWERS A QUESTION, it does not clean a string.
   *
   * It returns a boolean — "does this contain a space or a special character?" — so assigning
   * its result to the field set the value to `false` and the input became untypeable. Reject the
   * keystroke instead, which is what `CtaAuth` does and what makes the rule visible: the
   * character simply never appears.
   */
  function handleUsernameChange(e) {
    const { value } = e.target;
    if (checkSpecialChars(value)) return;

    setUsernameErr(null);
    setUsername(value);
  }

  async function handleLogin() {
    const res = await Api.post('/users/login', { email, password }, 'login', { silent: true });
    if (!res) return;

    if (res.pwErr) {
      setPassErr(res.pwErr);
      setAttempts(res.loginAttempts);
      passwordInputRef.current?.focus();
      return;
    }
    if (res.locked) {
      setAttempts(3);
      setAccountLockedVisible(true);
      return;
    }
    if (res.err) {
      emailInputRef.current?.focus();
      setEmailErr(res.err);
      return;
    }
    if (res.migratedUser) {
      navigate('/activate-account', { state: { email } });
      return;
    }
    // No token means an unhandled failure returned silently; show an inline error rather than
    // proceeding with an empty one.
    if (!res.token) {
      setEmailErr(t('error.somethingWentWrong'));
      return;
    }

    await storage.setItem('token', res.token);
    firebase.logEvent('web_login_from_rate_it_referral', { method: 'email' });
    onAuthComplete(res.user);
  }

  async function handleSignUp() {
    let user = createdUser;

    if (!user) {
      // Username goes with the signup so the backend rejects a taken handle up front and never
      // creates the account in that case.
      const res = await Api.post(
        '/users/signUp',
        { name: username, email, password, lang: language, username, sponsorConsent },
        'signUp',
        { silent: true }
      );
      if (!res) return;

      if (res.errs?.some((x) => x.input === 'username') || res.input === 'username') {
        setUsernameErr(res.errs?.find((x) => x.input === 'username')?.err || res.err);
        return;
      }
      if (res.err || res.errs || res.status) {
        setEmailErr(res.err || res.errs?.[0]?.err || t('error.somethingWentWrong'));
        return;
      }

      await storage.setItem('token', res.token);
      firebase.logEvent('web_sign_up_from_rate_it_referral', { method: 'email' });
      user = res.user;
      setCreatedUser(user);
    }

    // Complete onboarding inline, so the "choose a username" screen never interrupts a fan who
    // arrived to rate one specific moment.
    const usernameRes = await Api.post(
      '/users/updateUsername',
      { username, referralCode: '', onboardingStep: 2, type: 'fan' },
      'signUp',
      { silent: true }
    );
    if (!usernameRes) return;
    if (usernameRes.errs) {
      setUsernameErr(usernameRes.errs.find((x) => x.input === 'username')?.err);
      return;
    }
    if (usernameRes.err) {
      setUsernameErr(usernameRes.err);
      return;
    }
    if (usernameRes.status) {
      setUsernameErr(t('error.somethingWentWrong'));
      return;
    }

    onAuthComplete({ ...user, ...usernameRes, username });
  }

  return (
    <Overlay>
      <Sheet>
        {/* The offer first, in the same words the feed's banner uses — this screen interrupts a
            visitor mid-tap, and the reason it is worth completing has to lead. Dropped entirely
            for a non-referral entry rather than replaced with a generic pitch. */}
        {sender ? (
          <OfferBanner>
            {/* The SAME key the feed's banner uses — one sentence to translate, and the two
                surfaces cannot drift apart. The threshold is the shared constant rather than a
                literal 5, which this read before. */}
            <Trans components={{ strong: <strong /> }}>
              {t('tiri.rateIt.referral.banner', {
                defaultValue:
                  '<strong>@{{name}}</strong> invited you — rate {{count}} moments and you both earn {{points}} points.',
                name: sender.username || sender.name,
                count: RATE_IT_DEFAULT_MILESTONE_COUNT,
                points
              })}
            </Trans>
          </OfferBanner>
        ) : null}

        <Preview $src={item?.thumbnailUrl || item?.mediaUrl} />

        <BarPreview>
          <RateItRatingBar compact selected={pendingRating ?? null} disabled onRate={() => {}} />
        </BarPreview>

        <FormCard>
          <Title>
            {isLogin
              ? t('tiri.rateIt.auth.loginTitle', 'Sign In')
              : t('tiri.rateIt.auth.signUpTitle', 'Sign up to start rating')}
          </Title>
          <Subtitle>
            {isLogin
              ? t(
                  'tiri.rateIt.auth.loginSubtitle',
                  'Sign in to rate this moment and earn points with your friend.'
                )
              : t(
                  'tiri.rateIt.auth.signUpSubtitle',
                  'Create a free account on Playaz4Playaz to rate moments and earn points with your friend.'
                )}
          </Subtitle>

          <Fields>
            {!isLogin ? (
              <Input
                wid="100%"
                bg={RATE_IT_AUTH_COLORS.FIELD}
                placeholder={t('common.userName')}
                value={username}
                onChange={handleUsernameChange}
                error={usernameErr}
              />
            ) : null}

            <Input
              inputRef={emailInputRef}
              marg={isLogin ? 0 : '16px 0 0'}
              wid="100%"
              bg={RATE_IT_AUTH_COLORS.FIELD}
              placeholder={t('common.email')}
              value={email}
              onChange={(e) => {
                setEmailErr(null);
                setEmail(e.target.value.trim());
              }}
              error={emailErr}
            />

            <Input
              inputRef={passwordInputRef}
              marg="16px 0 0"
              wid="100%"
              bg={RATE_IT_AUTH_COLORS.FIELD}
              password
              placeholder={t('common.password')}
              value={password}
              onChange={(e) => {
                setPassErr(null);
                setPassword(e.target.value);
              }}
              error={passErr}
            />
          </Fields>

          {/* Sign-up only. `/users/login` has no such field and an existing account already
              answered this when it was created, so asking again on the sign-in tab would show a
              control that goes nowhere. Hidden on the username-retry pass too: the account is
              already created and the answer already recorded. */}
          {!isLogin && !usernameOnly ? (
            <ConsentRow>
              <ConsentBox
                id="rateItSponsorConsent"
                type="checkbox"
                checked={sponsorConsent}
                onChange={(e) => setSponsorConsent(e.target.checked)}
              />
              <ConsentLabel htmlFor="rateItSponsorConsent">{t('auth.sponsorConsent')}</ConsentLabel>
            </ConsentRow>
          ) : null}

          {isLogin && attempts ? <LoginAttempts attempts={attempts} /> : null}

          <Button
            big
            wid="100%"
            marg="24px 0 0"
            bg={theme.SECONDARY}
            disabled={disabled}
            text={
              isLogin ? t('common.login') : t('tiri.rateIt.auth.createAccount', 'Create Account')
            }
            onClick={isLogin ? handleLogin : handleSignUp}
          />

          {/* On BOTH modes. OAuth does not distinguish the two — the provider either knows this
              person or creates them — so offering these on sign-up only stranded fans who made
              their account with a provider and came back to sign in. */}
          <TextDivider>{t('common.or')}</TextDivider>

          {/* Social auth returns through the OAuth redirect rather than `onAuthComplete`, so the
              shared moment's path — query string included — has to travel with it. */}
          <GgLogin
            redirectUrl={returnPath}
            CustomButton={(props) => (
              <SocialButtonWrap>
                <Button google wid="100%" onClick={props.onClick} text={t('common.continue')} />
              </SocialButtonWrap>
            )}
          />

          {/* Kept although the design shows Google alone: an account created through Apple can
              only be signed into through Apple, so dropping the button would strand those fans
              on this screen with no route in. */}
          <AppleLogin
            redirectUrl={returnPath}
            CustomButton={(props) => (
              <SocialButtonWrap marg="16px 0 0">
                <Button apple wid="100%" onClick={props.onClick} text={t('common.continue')} />
              </SocialButtonWrap>
            )}
          />

          <SwitchMode>
            {isLogin
              ? t('tiri.rateIt.auth.needAccount', 'Need an account?')
              : t('tiri.rateIt.auth.haveAccount', 'Already have an account?')}
            <button type="button" onClick={onSwitchMode}>
              {isLogin
                ? t('tiri.rateIt.auth.signUp', 'Sign up')
                : t('tiri.rateIt.auth.signIn', 'Sign in')}
            </button>
          </SwitchMode>
        </FormCard>
      </Sheet>

      <AccountLocked
        visible={accountLockedVisible}
        closeModal={() => setAccountLockedVisible(false)}
      />
    </Overlay>
  );
};

export default RateItAuth;
